// Mi Estrategia — el playbook personal de Maxi (admin-only).
// Las LECCIONES de la bitácora agrupadas por tema (tags) + los HITOS del
// camino. No es una página técnica: es el documento vivo de su método y su
// psicología de trading — la materia prima de la biografía que algún día
// leerán sus hijos.

import { useMemo, useState, type ReactElement } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { AppLayout } from '../components/AppLayout';
import { cn } from '../lib/cn';
import {
  ScreenshotGrid,
  fmtFecha,
  type JournalEntry,
} from '../components/journal/shared';

interface ListResponse {
  total: number;
  entries: JournalEntry[];
}

// Temas con nombre presentable (el resto de tags se muestran tal cual).
const TEMA_LABEL: Record<string, string> = {
  psicologia: 'Psicología',
  'talon-de-aquiles': 'Talón de Aquiles',
  'gestion-riesgo': 'Gestión de riesgo',
  'cazar-movimiento': 'Cazar el movimiento',
  velas: 'Velas y estructura',
  general: 'General',
};

const tema = (tag: string): string =>
  TEMA_LABEL[tag] ?? tag.replace(/-/g, ' ').replace(/^\w/, (c) => c.toUpperCase());

export default function Estrategia(): ReactElement {
  const { data: lecciones, isLoading: l1 } = useQuery({
    queryKey: ['journal-lecciones'],
    queryFn: () => api<ListResponse>('/journal?kind=leccion&limit=100'),
  });
  const { data: hitos, isLoading: l2 } = useQuery({
    queryKey: ['journal-hitos'],
    queryFn: () => api<ListResponse>('/journal?kind=hito&limit=50'),
  });

  // Agrupar lecciones por tag (una lección con 2 tags aparece en ambos temas).
  const grupos = useMemo(() => {
    const by = new Map<string, JournalEntry[]>();
    for (const e of lecciones?.entries ?? []) {
      const ts = e.tags.length ? e.tags : ['general'];
      for (const t of ts) {
        if (!by.has(t)) by.set(t, []);
        by.get(t)!.push(e);
      }
    }
    return [...by.entries()].sort((a, b) => b[1].length - a[1].length);
  }, [lecciones]);

  const isLoading = l1 || l2;
  const vacio = !isLoading && grupos.length === 0 && (hitos?.entries.length ?? 0) === 0;

  return (
    <AppLayout>
      <div className="max-w-4xl mx-auto px-6 py-10">
        <div className="mb-3">
          <div className="text-xs text-ink-subtle tracking-[0.18em] uppercase mb-2">
            Playbook personal
          </div>
          <h1 className="text-3xl font-bold tracking-tight">Mi Estrategia</h1>
          <p className="text-ink-muted mt-2 max-w-2xl">
            Tus reglas, métodos y lecciones — lo aprendido en los libros, frente a los gráficos y
            en cada trade, ordenado por tema y con sus ejemplos reales.
          </p>
        </div>

        {/* El porqué de todo esto */}
        <div className="mb-10 rounded-xl border border-accent-500/25 bg-accent-500/[0.06] px-5 py-4">
          <p className="text-sm text-ink-muted leading-relaxed">
            <span className="text-accent-500 font-semibold">📖 El legado.</span> Este playbook se
            escribe solo, sesión a sesión. Algún día será el libro donde Renato y Santino lean cómo
            su papá aprendió a operar: la psicología, las reglas, las caídas y cómo se superaron.
          </p>
        </div>

        {isLoading ? (
          <div className="text-ink-subtle text-sm font-mono py-12 text-center">Cargando…</div>
        ) : vacio ? (
          <EmptyState />
        ) : (
          <>
            {/* Lecciones por tema */}
            <div className="space-y-10">
              {grupos.map(([tag, items]) => (
                <section key={tag}>
                  <div className="flex items-baseline gap-3 mb-4">
                    <h2 className="text-lg font-bold text-accent-500">{tema(tag)}</h2>
                    <span className="text-xs text-ink-subtle font-mono">
                      {items.length} {items.length === 1 ? 'lección' : 'lecciones'}
                    </span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {items.map((e) => (
                      <LeccionCard key={`${tag}-${e.id}`} entry={e} />
                    ))}
                  </div>
                </section>
              ))}
            </div>

            {/* Hitos del camino */}
            {(hitos?.entries.length ?? 0) > 0 && (
              <section className="mt-14">
                <h2 className="text-lg font-bold mb-1">🏁 Hitos del camino</h2>
                <p className="text-sm text-ink-muted mb-5">
                  Los momentos que marcaron el avance hacia la rentabilidad.
                </p>
                <div className="space-y-3 border-l-2 border-win-DEFAULT/40 pl-5 ml-1">
                  {hitos!.entries.map((h) => (
                    <div key={h.id} className="relative">
                      <span className="absolute -left-[27px] top-1.5 w-3 h-3 rounded-full bg-win-DEFAULT ring-4 ring-surface-base" />
                      <div className="text-[11px] text-ink-subtle font-mono">{fmtFecha(h.createdAt)}</div>
                      <h3 className="font-bold text-sm mt-0.5">{h.title}</h3>
                      <p className="text-sm text-ink-muted mt-1 whitespace-pre-line">{h.summary}</p>
                    </div>
                  ))}
                </div>
              </section>
            )}

            <div className="mt-12 text-center">
              <Link
                to="/bitacora"
                className="text-sm text-accent-500 hover:text-accent-600 font-semibold"
              >
                Ver la bitácora completa →
              </Link>
            </div>
          </>
        )}
      </div>
    </AppLayout>
  );
}

function LeccionCard({ entry }: { entry: JournalEntry }): ReactElement {
  const [expanded, setExpanded] = useState(false);
  const long = entry.summary.length > 280;
  const visible = !long || expanded ? entry.summary : entry.summary.slice(0, 280) + '…';
  return (
    <article className="bg-surface-elevated border border-surface-border rounded-xl p-5 hover:border-accent-500/30 transition-colors">
      <div className="flex items-center justify-between gap-2">
        <h3 className="font-bold text-sm">{entry.title}</h3>
        <time className="text-[11px] text-ink-subtle font-mono shrink-0">
          {fmtFecha(entry.createdAt).split(',')[0]}
        </time>
      </div>
      <p className="text-sm text-ink-muted mt-2 whitespace-pre-line leading-relaxed">{visible}</p>
      {long && (
        <button
          onClick={() => setExpanded((v) => !v)}
          className="text-xs text-accent-500 hover:text-accent-600 font-semibold mt-1"
        >
          {expanded ? 'Ver menos' : 'Ver completa'}
        </button>
      )}
      <ScreenshotGrid shots={entry.screenshots} />
      {entry.outcome && (
        <p className={cn('text-xs text-ink-subtle mt-3 pt-2 border-t border-surface-border/60 whitespace-pre-line')}>
          <span className="font-semibold uppercase tracking-wider text-[10px]">Cómo se aplicó: </span>
          {entry.outcome}
        </p>
      )}
    </article>
  );
}

function EmptyState(): ReactElement {
  return (
    <div className="text-center py-16 border border-dashed border-surface-border rounded-xl">
      <div className="text-4xl mb-3">🧭</div>
      <h3 className="font-bold">El playbook se está escribiendo</h3>
      <p className="text-sm text-ink-muted mt-1 max-w-md mx-auto">
        Cuando Njord registre lecciones en la bitácora (o las anotes tú), van apareciendo acá
        agrupadas por tema: psicología, gestión de riesgo, tus reglas.
      </p>
    </div>
  );
}
